import { ChangeEvent, useMemo, useState } from "react";
import { Axios, AxiosError } from "axios";
import { useMutation, useQuery } from "@tanstack/react-query";
import { z } from "zod";
import $api from "@/frontend/shared/api/axios";
import { useQueryParams } from "@/frontend/shared/lib/hooks/useQueryParams";
import { Input } from "@/frontend/shared/ui/Input";
import { NormalButton } from "@/frontend/shared/ui/Button";
import { Typography } from "@/frontend/shared/ui/Typography";
import { Checkbox } from "@/frontend/shared/ui/Checkbox";
import styles from "./FounderSignUpPage.module.scss";
import { snackbarConstructor } from "@/frontend/shared/ui/CustomNotifications/ui/Snackbar/snackbarConstructor";
import {
  getRouteExpertSignUp,
  getRouteFounderSignUp,
  getRoutePrivacyNotice,
  getRouteSignIn,
  getRouteTermsOfUse
} from "@/frontend/shared/const/router";
import { getInviteTokenInfo } from "../model/services/getInviteTokenInfo";
import { Loader } from "@/frontend/shared/ui/Loader/Loader";
import { useHistoricalNavigate } from "@/frontend/shared/lib/useHistoricalNavigate/useHistoricalNavigate";
import { Hint } from "@/frontend/shared/ui/Hint/Hint";
import { reportErrorToSentry } from "@/frontend/shared/lib/reportErrorToSentry/reportErrorToSentry";
import { ERoleAccess } from "@/frontend/app/providers/router/config/routeConfig";
import { useLocation } from "react-router-dom";
import { LogoSvg } from "@/frontend/shared/assets/LogoSvg";

// TODO: import this from repo-wide libs folder later -J
const PasswordSchema = z.string().min(8).max(32).regex(/[A-Z]/).regex(/[0-9]/);
const SignUpBody = (entity: ERoleAccess | null | undefined) =>
  z.object({
    token: z.string(),
    firstName: z.string().min(2),
    lastName: z.string().min(2),
    email: z.string().email(),
    password: PasswordSchema,
    organizationName: entity === "Expert" ? z.string().nullish() : z.string().min(2)
  });

export function FounderSignUpPage() {
  const { navigate } = useHistoricalNavigate();
  const [tokenError, setTokenError] = useState("");
  const [entity, setEntity] = useState<ERoleAccess | null | undefined>(null); // TODO: properly type this later - J

  const queryParams = useQueryParams();
  const token = queryParams.get("token") as string;

  const { data, isLoading } = useQuery({
    queryKey: ["signUpData"],
    queryFn: async () => {
      try {
        const tokenInfo = await getInviteTokenInfo(token);
        setEmail(tokenInfo.email);
        setEntity(tokenInfo?.entity);
        setTokenError("");

        if (tokenInfo?.entity === ERoleAccess.FOUNDER) {
          navigate(`${getRouteFounderSignUp()}?token=${token}`, { replace: true });
        }

        if (tokenInfo?.entity === ERoleAccess.EXPERT) {
          navigate(`${getRouteExpertSignUp()}?token=${token}`, { replace: true });
        }

        // VC-VC invite - J
        if (tokenInfo?.organizationName) {
          setOrganizationName(tokenInfo.organizationName);
          setIsOrganizationPreFilled(true);
        }

        // VC-Founder invite - M
        if (tokenInfo?.companyName) {
          setOrganizationName(tokenInfo.companyName);
          setIsOrganizationPreFilled(true);
        }

        // Innovera-VC invite - J
        if (tokenInfo?.investor?.name) {
          setOrganizationName(tokenInfo?.investor?.name);
          setIsOrganizationPreFilled(true);
        }

        return tokenInfo;
      } catch (error) {
        const axiosError = error as AxiosError<{ message: string }>;

        setTokenError("Token is invalid or expired");
        snackbarConstructor("Token is invalid or expired", "error");
      }
    },
    refetchOnWindowFocus: false
  });

  const [firstName, setFirstName] = useState<string>("");
  const [lastName, setLastName] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  // const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [organizationName, setOrganizationName] = useState<string>("");
  // const [isAcceptTerms, setIsAcceptTerms] = useState<boolean>(false);
  // const [isAcceptPrivacy, setIsAcceptPrivacy] = useState<boolean>(false);
  const [isOrganizationPreFilled, setIsOrganizationPreFilled] = useState<boolean>(false);

  const _validPassword = PasswordSchema.safeParse(password).success;
  // const validPasswords = _validPassword && password === confirmPassword;

  const isPasswordLengthValid = password.length >= 8;
  const isPasswordHasOneNumber = password.match(/[0-9]/) !== null;
  const isPasswordHasOneCapitalLetter = password.match(/[A-Z]/) !== null;

  const hasValidInput = SignUpBody(entity).safeParse({
    token,
    firstName,
    lastName,
    email,
    password,
    organizationName
  }).success;

  const mutation = useMutation({
    mutationKey: ["sign-up-invite"],
    mutationFn: async (payload: any) => {
      const response = await $api.post(`/auth/register?inviteToken=${token}`, payload);
      return response.data;
    },
    onSuccess: () => {
      window?.location?.reload();
    },
    onError: (err: AxiosError<{ message: string }>) => {
      const message =
        err.response?.data?.message ??
        err?.message ??
        "Failed to create account. Try again later or contact the administrator.";
      snackbarConstructor(message, "error");
      reportErrorToSentry(message, "/auth/register");
    }
  });

  const onChangeSetLastName = (e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    setLastName(e.target.value);
  };

  const onChangeSetEmail = (e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    setEmail(e.target.value.trim());
  };

  const onChangeSetPassword = (e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    setPassword(e.target.value.trim());
  };

  // const onChangeSetConfirmPassword = (e: ChangeEvent<HTMLInputElement>) => {
  //   e.preventDefault();
  //   setConfirmPassword(e.target.value.trim());
  // };

  const onChangeSetOrganizationName = (e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    setOrganizationName(e.target.value);
  };

  // const toggleTerms = () => {
  //   setIsAcceptTerms((terms) => !terms);
  // };

  // const togglePrivacy = () => {
  //   setIsAcceptPrivacy((privacy) => !privacy);
  // };

  const signUp = async () => {
    if (!hasValidInput) return;
    if (tokenError) return snackbarConstructor(tokenError, "error");

    const payload = {
      token,
      firstName,
      lastName,
      email,
      password
    };

    if (entity !== ERoleAccess.EXPERT) {
      // @ts-expect-error
      payload.organizationName = organizationName;
    }

    await mutation.mutateAsync(payload);
  };

  const content = useMemo(() => {
    if (isLoading) {
      return (
        <div style={{ height: "332px", display: "flex", justifyContent: "center", alignItems: "center" }}>
          <Loader />
        </div>
      );
    } else {
      return (
        <div className={styles.form}>
          <div className={styles.names}>
            <div className={styles.inputField}>
              <Typography variant="label-4" color="greyscale700">
                First Name
              </Typography>
              <Input type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)} inputSize="small" />
            </div>
            <div className={styles.inputField}>
              <Typography variant="label-4" color="greyscale700">
                Last Name
              </Typography>
              <Input type="text" value={lastName} onChange={onChangeSetLastName} inputSize="small" />
            </div>
          </div>
          <div className={styles.inputField}>
            <Typography variant="label-4" color="greyscale700">
              Email
            </Typography>
            <Input type="text" value={email} onChange={onChangeSetEmail} disabled={!!data?.email} inputSize="small" />
          </div>

          {entity !== "Expert" && (
            <div className={styles.inputField}>
              <Typography variant="label-4" color="greyscale700">
                Organization Name
              </Typography>
              <Input
                type="text"
                inputSize="small"
                value={organizationName}
                onChange={onChangeSetOrganizationName}
                disabled={!!data?.organizationName || isOrganizationPreFilled}
              />
            </div>
          )}
          <div className={styles.inputField}>
            <Typography variant="label-4" color="greyscale700">
              Password
            </Typography>
            <Input type="password" inputSize="small" value={password} onChange={onChangeSetPassword} />
          </div>
          <div className={styles.passwordHints}>
            <Hint label="At least 8 characters" isActive={isPasswordLengthValid} />

            <Hint label="At least 1 number" isActive={isPasswordHasOneNumber} />

            <Hint label="At least 1 uppercase letter" isActive={isPasswordHasOneCapitalLetter} />
          </div>

          {/* <Input
            type="password"
            placeholder="Confirm password"
            containerStyle={{ marginBottom: "1rem" }}
            value={confirmPassword}
            onChange={onChangeSetConfirmPassword}
          /> */}
        </div>
      );
    }
  }, [data, isLoading, firstName, lastName, email, password, organizationName, entity]);

  return (
    <div className={styles.container}>
      <LogoSvg className={styles.logo} />
      <div className={styles.contentContainer}>
        <div className={styles.titleContainer}>
          <Typography variant="heading-5" color="greyscale900" className={styles.title}>
            Welcome to Innovera
          </Typography>
          <div className={styles.haveAccount}>
            <Typography variant="body-2" color="greyscale600">
              Already have an account?
            </Typography>
            <Typography
              variant="body-2"
              color="innovera-orange500"
              onClick={() => navigate(getRouteSignIn())}
              className={styles.logInButton}
            >
              Log in
            </Typography>
          </div>
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            signUp();
          }}
          className={styles.formContainer}
        >
          {content}
          <div className={styles.bottomContainer}>
            <NormalButton
              className={styles.signUpButton}
              isDisabled={!hasValidInput}
              variant="primary"
              size="large"
              isSubmitting={mutation.isPending}
              type="submit"
            >
              Sign Up
            </NormalButton>
            {tokenError && (
              <Typography variant="body-3" color="error-red" className={styles.tokenError}>
                {tokenError}
              </Typography>
            )}
            <div className={styles.termsContainer}>
              <Typography variant="body-3" color="greyscale600">
                By clicking "Sign Up" above, you acknowledge that you have read, understood, and agreed to Innovera’s{" "}
                <span onClick={() => navigate(getRouteTermsOfUse())}>Terms & Conditions</span> and{" "}
                <span onClick={() => navigate(getRoutePrivacyNotice())}>Privacy Policy</span>
              </Typography>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
